import { throwInternalError } from './errors'
import { Json, isPojo } from './tools'

export interface IpcValueSerializer {
  typeName: string
  canSerialize(value: unknown): boolean
  serialize(value: unknown): string
  deserialize(value: string): unknown
}

const MAGIC = '※'

function buildSerializedValue(serializerType: string, value: string): string {
  return `${MAGIC}${serializerType}${MAGIC}${value}${MAGIC}`
}

const SERIALIZED_VALUE_RX = new RegExp(`^${MAGIC}([a-zA-Z]+)${MAGIC}(.*)${MAGIC}$`)

function detectSerializedValue(value: string): { serializerType: string; value: string } | undefined {
  const m = SERIALIZED_VALUE_RX.exec(value)
  if (!m) return
  return {
    serializerType: m[1],
    value: m[2],
  }
}

function isSimpleJSValue(value: unknown): value is undefined | null | boolean | number | string {
  return (
    value === undefined ||
    value === null ||
    typeof value === 'boolean' ||
    typeof value === 'number' ||
    typeof value === 'string'
  )
}

export function serializeValueToJson(value: unknown, serializers: IpcValueSerializer[]): Json {
  if (isSimpleJSValue(value)) return value
  if (value instanceof Array) {
    return value.map(v => serializeValueToJson(v, serializers))
  }
  if (isPojo(value)) {
    const obj: { [key: string]: Json } = {}
    for (const key of Object.keys(value)) {
      obj[key] = serializeValueToJson(value[key], serializers)
    }
    return obj
  }
  for (const serializer of serializers) {
    if (serializer.canSerialize(value)) {
      return buildSerializedValue(serializer.typeName, serializer.serialize(value))
    }
  }
  throwInternalError(`<7a16cc71> Cannot serialize value: typeof=${typeof value}`)
}

export function deserializeValueFromJson(value: Json, serializers: IpcValueSerializer[]): unknown {
  if (typeof value === 'string') {
    const r = detectSerializedValue(value)
    if (r) {
      const s = serializers.find(s => s.typeName === r.serializerType)
      if (!s) throwInternalError(`<b2f1aa22> Serializer with type=${r.serializerType} not registered`)
      return s.deserialize(r.value)
    }
  }
  if (isSimpleJSValue(value)) {
    return value
  }
  if (value instanceof Array) {
    return value.map(v => deserializeValueFromJson(v, serializers))
  }
  if (isPojo(value)) {
    const obj: { [key: string]: unknown } = {}
    for (const key of Object.keys(value)) {
      obj[key] = deserializeValueFromJson(value[key], serializers)
    }
    return obj
  }
  throwInternalError(`<2f3aea58> Cannot deserialize value: typeof=${typeof value}`)
}
